import { useSetJobRecruiters, useUsers } from "@vidario/api-client";
import {
  countLabel,
  EXTRA_RECRUITER_NOUN,
  type Job,
  type User,
  searchByNameAndEmail,
  userInitials,
} from "@vidario/domain";
import { Avatar, Button, Combobox, Modal, useToast } from "@vidario/ui";
import { X } from "lucide-react";
import { useMemo, useState, type ReactElement } from "react";

export interface RecruitersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
}

/** Admin-only modal for managing which recruiters have access to a
 * stelle beyond its owner (`job.createdBy`). The owner is shown
 * read-only as the "Inhaber". */
export function RecruitersDialog({ open, onOpenChange, job }: RecruitersDialogProps): ReactElement {
  const toast = useToast();
  const { data: users = [] } = useUsers();
  const setRecruiters = useSetJobRecruiters();

  // Edit buffer = either an in-progress local edit (array) or `null`
  // when nothing has been touched since the last open. While `null`,
  // the displayed list mirrors the live `job.assignedRecruiters` —
  // which means re-opening the modal after a save always reflects
  // the latest persisted state without needing a setState in an
  // effect. Reset to `null` on close.
  const [edits, setEdits] = useState<string[] | null>(null);
  const [query, setQuery] = useState("");
  const assigned: string[] = edits ?? job.assignedRecruiters;

  const owner = useMemo(
    () => users.find((u) => u.id === job.createdBy) ?? null,
    [users, job.createdBy],
  );
  const assignedUsers = useMemo(
    () => assigned.map((id) => users.find((u) => u.id === id)).filter((u): u is User => Boolean(u)),
    [assigned, users],
  );
  // Pickable = every user except the owner and the ones already assigned.
  const pickable = useMemo(
    () => users.filter((u) => u.id !== job.createdBy && !assigned.includes(u.id)),
    [users, job.createdBy, assigned],
  );

  const matches = useMemo(() => searchByNameAndEmail(pickable, query), [pickable, query]);

  const add = (id: string): void => {
    if (!id || assigned.includes(id)) return;
    setEdits([...assigned, id]);
  };
  const remove = (id: string): void => {
    setEdits(assigned.filter((x) => x !== id));
  };
  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      setEdits(null);
      setQuery("");
    }
    onOpenChange(next);
  };

  const save = (): void => {
    setRecruiters.mutate(
      { id: job.id, userIds: assigned },
      {
        onSuccess: () => {
          toast.push({
            kind: "success",
            title: "Recruiter aktualisiert",
            message: `${countLabel(assignedUsers.length, EXTRA_RECRUITER_NOUN)} zugewiesen.`,
          });
          handleOpenChange(false);
        },
        onError: (err) => {
          toast.push({
            kind: "error",
            title: "Speichern fehlgeschlagen",
            message: err.message,
          });
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="Recruiter verwalten"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={save} loading={setRecruiters.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <section>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-text-tertiary">
            Inhaber
          </div>
          {owner ? (
            <div className="flex items-center gap-3 rounded-md border border-border-default bg-neutral-50 p-3">
              <Avatar initials={userInitials(owner.name)} size={32} />
              <div className="flex-1">
                <div className="text-sm font-medium">{owner.name}</div>
                <div className="text-xs text-text-tertiary">{owner.email}</div>
              </div>
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                Inhaber
              </span>
            </div>
          ) : (
            <div className="text-sm text-text-tertiary">Inhaber unbekannt</div>
          )}
        </section>

        <section>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-text-tertiary">
            Zusätzliche Recruiter ({assignedUsers.length})
          </div>
          {assignedUsers.length === 0 ? (
            <div className="rounded-md border border-dashed border-border-default p-3 text-sm text-text-tertiary">
              Noch keine weiteren Recruiter zugewiesen.
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {assignedUsers.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center gap-3 rounded-md border border-border-default p-2"
                >
                  <Avatar initials={userInitials(u.name)} size={28} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{u.name}</div>
                    <div className="text-xs text-text-tertiary">{u.email}</div>
                  </div>
                  <Button
                    variant="destructive-ghost"
                    size="sm"
                    onClick={() => remove(u.id)}
                    aria-label={`${u.name} entfernen`}
                    leftIcon={<X size={14} />}
                  >
                    Entfernen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <Combobox
            label="Recruiter hinzufügen"
            placeholder={
              pickable.length === 0 ? "Keine weiteren verfügbar" : "Name oder E-Mail eingeben …"
            }
            disabled={pickable.length === 0}
            items={matches}
            itemKey={(u) => u.id}
            itemToString={(u) => `${u.name} · ${u.email}`}
            renderItem={(u) => (
              <>
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-text-tertiary">{u.email}</div>
              </>
            )}
            inputValue={query}
            onInputValueChange={setQuery}
            onSelect={(u) => add(u.id)}
            clearOnSelect
          />
        </section>
      </div>
    </Modal>
  );
}
